import { faCheck, faTimes } from '@fortawesome/free-solid-svg-icons'
import { FontAwesomeIcon } from '@fortawesome/react-fontawesome'
import { DesignDocumentResponse, ProductSearchResult } from 'models/SearchResult'
import { primitiveArrayEquals } from 'utils/Utils'
import React from 'react'
import isEqual from 'lodash.isequal'
import { initialPipeOpening, ProductFormState } from 'hooks/useProductForm'
import { DesignDocumentFormState } from 'hooks/useDesignDocumentsForm'

interface ProductDiff {
    description: boolean,
    tags: boolean,
    drawings: boolean,
    toe: boolean,
    handrail: boolean,
    steelworkCode: boolean,
    secondaryCasting: boolean,
    reinforcing: boolean,
    pipeOpenings: boolean,
    weight: boolean,
    flowControlDevices: boolean    
}

const toDesignDocumentResponse = (doc: DesignDocumentFormState): DesignDocumentResponse => {
    
    return {
        category: doc.category.category,
        asset: {
            assetUrl: doc.asset.sourceUrl,
            assetFileName: doc.asset.fileName,
            assetFileExtension: doc.asset.fileExtension,
            assetPath: doc.asset.filePath,
        }
    }
}

export const isAllDrawingsDifferent = (a: DesignDocumentResponse[], b: DesignDocumentFormState[]): boolean => {

    const setA: Set<string> = new Set(a.map(_ => _.asset.assetUrl))
    const setB: Set<string> = new Set(b.map(_ => toDesignDocumentResponse(_).asset.assetUrl))
    const intersection = new Set([...setA].filter(x => setB.has(x)))
    const areAllDifferent: boolean = intersection.size === 0

    return areAllDifferent
}


export const diffProductSearchResultAndProductFormState = (a: ProductSearchResult, pfs: ProductFormState, dds: DesignDocumentFormState[]): ProductDiff => {

    const { weight, ...pfsWithoutWeight } = pfs
   
    const b: ProductSearchResult = {
            ...pfsWithoutWeight,
            objectID: pfs.productCode,
            ...(weight && { weight: Number(weight) }),
            pipeOpenings: pfs.pipeOpenings.map(po => {
                return {
                    pipeId: po.pipeId,
                    insideDiameter: po.insideDiameter,
                    material: po.material,
                    outsideDiameter: po.outsideDiameter, 
                    openingSize: po.openingSize,
                    invertLevel: po.invertLevel,
                    openingLevel: po.openingLevel,
                    ...(po.pipeType && { pipeType: po.pipeType }),
                    ...(po.pipeType === 'CUSTOM' && { height: po.height }),
                    ...(po.pipeType === 'CUSTOM' && { width: po.width })
                }
            }),
            designDocuments: dds.map(_ => toDesignDocumentResponse(_))
        }
    
    return {
        description: a.description === b.description,
        tags: primitiveArrayEquals(a.tags, b.tags),
        drawings: isEqual(a.designDocuments, b.designDocuments),
        toe: a.toe === b.toe,
        handrail: a.handrail === b.handrail,
        steelworkCode: primitiveArrayEquals(a.steelworkCode,b.steelworkCode),
        secondaryCasting: primitiveArrayEquals(a.secondaryCasting, b.secondaryCasting) ,
        reinforcing: primitiveArrayEquals(a.reinforcing, b.reinforcing),
        // First predicate captures empty form state case
        pipeOpenings: pfs.pipeOpenings.every(_ => isEqual(_, initialPipeOpening)) || isEqual(a.pipeOpenings, b.pipeOpenings),
        weight: isEqual(a.weight, b.weight),
        flowControlDevices: isEqual(a.flowControlDevices, b.flowControlDevices)   
    }

}

export const diffBetweenProductSearchResults = (a: ProductSearchResult, b: ProductSearchResult): ProductDiff => {
    return {
        description: a.description === b.description,
        tags: primitiveArrayEquals(a.tags, b.tags),
        drawings: isEqual(a.designDocuments, b.designDocuments),
        toe: a.toe === b.toe,
        handrail: a.handrail === b.handrail,
        steelworkCode: primitiveArrayEquals(a.steelworkCode,b.steelworkCode),
        secondaryCasting: primitiveArrayEquals(a.secondaryCasting, b.secondaryCasting) ,
        reinforcing: primitiveArrayEquals(a.reinforcing, b.reinforcing),
        pipeOpenings: isEqual(a.pipeOpenings, b.pipeOpenings),
        weight: isEqual(a.weight, b.weight),
        flowControlDevices: isEqual(a.flowControlDevices, b.flowControlDevices)   
    }    
}   

const WhatsChanged = ({ productDiff }: { productDiff: ProductDiff }) => {

    const RedCrossIcon = <FontAwesomeIcon className={`pr-2`} color='red' icon={faTimes} />
    const GreenCheckIcon = <FontAwesomeIcon className={`pr-2`} color='green' icon={faCheck} />

    const chooseAppropriateIcon = (hasChanged: boolean) => (
        hasChanged ? RedCrossIcon : GreenCheckIcon 
    )

    return (
        <div className="field">
            <label className="label">What's changed?</label>

            Product Description: {chooseAppropriateIcon(productDiff.description)}
            Tags: {chooseAppropriateIcon(productDiff.tags)}
            Drawings: {chooseAppropriateIcon(productDiff.drawings)}
            Toe: {chooseAppropriateIcon(productDiff.toe)}
            Handrail: {chooseAppropriateIcon(productDiff.handrail)}
            Steelwork Code: {chooseAppropriateIcon(productDiff.steelworkCode)}
            Secondary Casting: {chooseAppropriateIcon(productDiff.secondaryCasting)}
            Reinforcing: {chooseAppropriateIcon(productDiff.reinforcing)}
            Pipe Openings: {chooseAppropriateIcon(productDiff.pipeOpenings)}
            Weight: {chooseAppropriateIcon(productDiff.weight)}
            Flow Control Devices: {chooseAppropriateIcon(productDiff.flowControlDevices)}
        </div>
    )
}

export { WhatsChanged }